import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { App, Button, Flex, Steps } from "antd";
import { useMemo, useState } from "react";
import { isMaterialUsageRecorded } from "../../../../common/utils/serviceMaterials";
import type { TicketData } from "../TicketViewRender";
import styles from "./CreateReportWizard.module.css";
import { ReportDetailsPreview } from "./ReportDetailsPreview";
import { ReportPicturePicker } from "./ReportPicturePicker";
import { ReportSummaryStep } from "./ReportSummaryStep";
import { ReportSuppliesPreview } from "./ReportSuppliesPreview";
import { ReportTaskStep } from "./ReportTaskStep";
import {
  GENERAL_PICTURES_KEY,
  type ReportPictureSelection,
  groupReportPictures,
  reportFilename,
  sortTasksForReport,
} from "./reportPictures";
import { createWorkOrderReport } from "./store";

type ReportPhase = "details" | "tasks" | "general" | "supplies" | "create";

// One page per screen of the wizard; the Tasks phase has a page for every task.
type ReportPage = { phase: ReportPhase; taskIndex?: number };

type CreateReportWizardProps = {
  ticketData: TicketData;
  dispatch: Dispatch;
  intl: IntlShape;
  onClose: () => void;
};

export function CreateReportWizard({
  ticketData,
  dispatch,
  intl,
  onClose,
}: CreateReportWizardProps) {
  const { message } = App.useApp();
  const { ticket, serviceObjects, materialCatalog, categoryCatalog } = ticketData;
  const tasks = useMemo(() => sortTasksForReport(ticket.items ?? []), [ticket.items]);
  const picturesByKey = useMemo(() => groupReportPictures(ticket), [ticket]);
  const generalPictures = picturesByKey[GENERAL_PICTURES_KEY] ?? [];
  const showGeneralPictures = generalPictures.length > 0;

  const [pageIndex, setPageIndex] = useState(0);
  const [selection, setSelection] = useState<ReportPictureSelection>({});
  const [creating, setCreating] = useState(false);

  const pages = useMemo((): ReportPage[] => {
    const taskPages = tasks.map((_, taskIndex) => ({
      phase: "tasks" as const,
      taskIndex,
    }));
    return [
      { phase: "details" },
      ...(taskPages.length > 0 ? taskPages : [{ phase: "tasks" as const }]),
      ...(showGeneralPictures ? [{ phase: "general" as const }] : []),
      { phase: "supplies" },
      { phase: "create" },
    ];
  }, [tasks, showGeneralPictures]);

  const phases: ReportPhase[] = [
    "details",
    "tasks",
    ...(showGeneralPictures ? (["general"] as const) : []),
    "supplies",
    "create",
  ];
  const page = pages[pageIndex];
  const isLastPage = pageIndex === pages.length - 1;

  const selectedFor = (key: string) => selection[key] ?? [];
  const setSelectedFor = (key: string) => (fileIds: string[]) =>
    setSelection((previous) => ({ ...previous, [key]: fileIds }));

  const taskPictureCount = tasks.reduce(
    (count, task) => count + (task.id ? selectedFor(task.id).length : 0),
    0
  );
  const tasksWithPictures = tasks.filter(
    (task) => task.id && selectedFor(task.id).length > 0
  ).length;
  const filename = reportFilename(ticket);

  const phaseTitles: Record<ReportPhase, string> = {
    details: intl.formatMessage({ id: "wo_report_step_details" }),
    tasks: intl.formatMessage({ id: "wo_report_step_tasks" }),
    general: intl.formatMessage({ id: "wo_report_step_general_pictures" }),
    supplies: intl.formatMessage({ id: "wo_report_step_supplies" }),
    create: intl.formatMessage({ id: "wo_report_step_create" }),
  };

  const createReport = () => {
    if (!ticket.id) return;
    setCreating(true);
    dispatch(
      createWorkOrderReport({
        ticketId: ticket.id,
        selection: {
          tasks: tasks.flatMap((task) =>
            task.id ? [{ task: task.id, files: selectedFor(task.id) }] : []
          ),
          general_files: selectedFor(GENERAL_PICTURES_KEY),
        },
        fallbackFilename: filename,
        onSuccess: () => {
          setCreating(false);
          message.success(intl.formatMessage({ id: "wo_report_created" }));
          onClose();
        },
        onError: () => {
          setCreating(false);
          message.error(intl.formatMessage({ id: "wo_report_failed" }));
        },
      })
    );
  };

  const renderPage = () => {
    switch (page.phase) {
      case "details":
        return <ReportDetailsPreview ticket={ticket} intl={intl} />;
      case "tasks": {
        const task = page.taskIndex != null ? tasks[page.taskIndex] : undefined;
        if (!task?.id) {
          return intl.formatMessage({ id: "wo_report_no_tasks" });
        }
        return (
          <ReportTaskStep
            key={task.id}
            task={task}
            position={(page.taskIndex ?? 0) + 1}
            taskCount={tasks.length}
            pictures={picturesByKey[task.id] ?? []}
            selectedFileIds={selectedFor(task.id)}
            onChange={setSelectedFor(task.id)}
            serviceObjects={serviceObjects}
            intl={intl}
          />
        );
      }
      case "general":
        return (
          <ReportPicturePicker
            pictures={generalPictures}
            selectedFileIds={selectedFor(GENERAL_PICTURES_KEY)}
            onChange={setSelectedFor(GENERAL_PICTURES_KEY)}
            emptyText={intl.formatMessage({ id: "wo_report_task_no_pictures" })}
            intl={intl}
          />
        );
      case "supplies":
        return (
          <ReportSuppliesPreview
            materials={ticket.materials ?? []}
            materialCatalog={materialCatalog}
            categoryCatalog={categoryCatalog}
            intl={intl}
          />
        );
      case "create":
        return (
          <ReportSummaryStep
            filename={filename}
            taskCount={tasks.length}
            tasksWithPictures={tasksWithPictures}
            taskPictureCount={taskPictureCount}
            generalPictureCount={selectedFor(GENERAL_PICTURES_KEY).length}
            showGeneralPictures={showGeneralPictures}
            usedSupplyCount={
              (ticket.materials ?? []).filter(isMaterialUsageRecorded).length
            }
            intl={intl}
          />
        );
    }
  };

  return (
    <Flex vertical gap="large" className={styles.wizard}>
      <Steps
        size="small"
        current={phases.indexOf(page.phase)}
        onChange={(phaseIndex) =>
          setPageIndex(
            pages.findIndex((candidate) => candidate.phase === phases[phaseIndex])
          )
        }
        items={phases.map((phase) => ({
          title: phaseTitles[phase],
          disabled: creating,
        }))}
      />
      <Flex vertical className={styles.page}>
        {renderPage()}
      </Flex>
      <Flex justify="space-between" gap="small" className={styles.footer}>
        <Button onClick={onClose} disabled={creating}>
          {intl.formatMessage({ id: "wo_report_cancel" })}
        </Button>
        <Flex gap="small">
          <Button
            disabled={pageIndex === 0 || creating}
            onClick={() => setPageIndex((index) => index - 1)}
          >
            {intl.formatMessage({ id: "wo_report_back" })}
          </Button>
          {isLastPage ? (
            <Button type="primary" loading={creating} onClick={createReport}>
              {intl.formatMessage({ id: "wo_report_create_pdf" })}
            </Button>
          ) : (
            <Button type="primary" onClick={() => setPageIndex((index) => index + 1)}>
              {intl.formatMessage({ id: "wo_report_next" })}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
